import {
  SystemEntity,
  TaskTypeViewSchema,
  type TaskTypeView,
} from '@erp/shared-types';

/** Shown when a field or match has no value. */
export const EMPTY_MARK = '—';

/** Schema field summary the review page already has on the task type. */
export type TaskViewSchemaField = {
  fieldName: string;
  fieldType: string;
  label: string;
  isMultiple?: boolean;
  enumValues?: string[] | null;
  referencedEntityType?: SystemEntity | null;
  children?: TaskViewSchemaField[];
};

export type EntityReferenceValue = {
  entityType: SystemEntity;
  entityId: string;
};

/** `{ entityType, entityId }` as stored on an `entity_reference` field. */
export function readEntityReference(
  value: unknown,
): EntityReferenceValue | null {
  if (!isRecord(value)) return null;
  const entityType = value.entityType;
  const entityId = value.entityId;
  if (typeof entityType !== 'string' || typeof entityId !== 'string') {
    return null;
  }
  if (!isSystemEntity(entityType)) return null;
  const id = entityId.trim();
  if (!id) return null;
  return { entityType, entityId: id };
}

function isSystemEntity(value: string): value is SystemEntity {
  return (Object.values(SystemEntity) as string[]).includes(value);
}

export type TaskTypeViewSlot =
  | { kind: 'absent' }
  | { kind: 'empty'; reason: 'invalid'; detail: string | null }
  | { kind: 'ready'; view: TaskTypeView };

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Value a workflow should submit: the correction when one exists, else input. */
export function taskWorkingCopy(task: {
  correction?: Record<string, unknown> | null;
  input?: Record<string, unknown> | null;
}): Record<string, unknown> {
  return task.correction ?? task.input ?? {};
}

export function taskInputRecord(
  input: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return input ?? {};
}

export function getValueAtPath(
  root: unknown,
  path: readonly string[],
): unknown {
  let current = root;
  for (const segment of path) {
    if (!isRecord(current)) return undefined;
    current = current[segment];
  }
  return current;
}

/** Immutable update. Sibling keys and untouched nested values stay put. */
export function setValueAtPath(
  root: Record<string, unknown>,
  path: readonly string[],
  value: unknown,
): Record<string, unknown> {
  const [head, ...rest] = path;
  if (!head) return root;
  if (rest.length === 0) return { ...root, [head]: value };
  const child = root[head];
  const nested = isRecord(child) ? child : {};
  return { ...root, [head]: setValueAtPath(nested, rest, value) };
}

export function readRowArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function rowObject(row: unknown): Record<string, unknown> {
  return isRecord(row) ? row : {};
}

/** Replace one row. Every other row is the same reference. */
export function replaceRow(
  rows: readonly unknown[],
  index: number,
  next: Record<string, unknown>,
): unknown[] {
  return rows.map((row, rowIndex) => (rowIndex === index ? next : row));
}

export function findSchemaField(
  fields: readonly TaskViewSchemaField[],
  path: readonly string[],
): TaskViewSchemaField | null {
  let current = fields;
  let found: TaskViewSchemaField | undefined;
  for (const segment of path) {
    found = current.find((field) => field.fieldName === segment);
    if (!found) return null;
    current = found.children ?? [];
  }
  return found ?? null;
}

export function placementLabel(
  label: string | null,
  field: TaskViewSchemaField | null,
  path: readonly string[],
): string {
  if (label && label.trim()) return label;
  if (field?.label) return field.label;
  return path[path.length - 1] ?? '';
}

/**
 * A value as text for a control or a cell. Null for nothing to show.
 * Booleans are left to the caller, which may want to localize them.
 */
export function valueText(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number' || typeof value === 'string') {
    return String(value);
  }
  if (typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

/** Null and undefined are the same empty. Anything else is a real difference. */
export function scalarValuesDiffer(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return false;
  return !(left == null && right == null);
}

export function idAtPath(
  root: unknown,
  path: readonly string[],
): string | null {
  const value = getValueAtPath(root, path);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

type ViewSource =
  | {
      view?: unknown;
      viewError?: string | null;
      inputSchema?: unknown;
    }
  | null
  | undefined;

function hasStoredView(
  taskType: ViewSource,
): taskType is NonNullable<ViewSource> {
  if (!taskType) return false;
  return taskType.view != null || Boolean(taskType.viewError);
}

function slotFromParsedView(
  taskType: NonNullable<ViewSource>,
): TaskTypeViewSlot {
  const parsed = TaskTypeViewSchema.safeParse(taskType.view);
  if (!parsed.success) {
    return {
      kind: 'empty',
      reason: 'invalid',
      detail: parsed.error.issues[0]?.message ?? null,
    };
  }
  return { kind: 'ready', view: parsed.data };
}

/**
 * Absent means the review page keeps today's input card. A stored view that
 * cannot render still occupies the view slot so the raw input card stays hidden.
 */
export function resolveTaskTypeViewSlot(
  taskType: ViewSource,
): TaskTypeViewSlot {
  if (!hasStoredView(taskType)) return { kind: 'absent' };
  if (taskType.viewError) {
    return { kind: 'empty', reason: 'invalid', detail: taskType.viewError };
  }
  return slotFromParsedView(taskType);
}

export function showsTaskInputCard(slot: TaskTypeViewSlot): boolean {
  return slot.kind === 'absent';
}
